import React, { useCallback } from "react";
import { gql, useQuery, useSubscription } from "@apollo/client";
import {
  Transaction,
  TransactionPage,
  QueryFindTransactionsByUserIdArgs
} from "../../../../schemaTypes";
import { newTransactionSubscription } from "../FindTransactions";

export const findTransactionsByUserIdQuery = gql`
  query FindTransactionsByUserId(
    $userId: String!
    $page: Int!
    $pageSize: Int!
    $statusFilter: String
    $typeFilter: String
    $searchText: String
    $sortBy: String
    $sortOrder: String
  ) {
    findTransactionsByUserId(
      userId: $userId
      page: $page
      pageSize: $pageSize
      statusFilter: $statusFilter
      typeFilter: $typeFilter
      searchText: $searchText
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        id
        amount
        charge
        reference
        type
        status
        currency
        transferTypes
        # wallet {
        #   id
        #   balance
        #   owner {
        #     id
        #     customer {
        #       firstname
        #       lastname
        #     }
        #   }
        #   createdAt
        #   updatedAt
        # }
        # recipientWallet {
        #   id
        #   balance
        #   currency
        #   owner {
        #     admin {
        #       merchantProfile {
        #         companyName
        #       }
        #     }
        #   }
        #   createdAt
        #   updatedAt
        # }
        bankPreference {
          id
        }
        offer {
          id
          title
          description
          category{
            name
          }
        }
        njoyCode
        partnerVoucher
        user {
          id
          # createdAt
          # updatedAt
        }
        createdAt
        updatedAt
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

export interface WithFindTransactionsByUserId {
  transactions: Transaction[];
  loading: boolean;
  loadMore: () => void;
  hasMoreTransactions: boolean;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface Props {
  variables: QueryFindTransactionsByUserIdArgs;
  children: (data: WithFindTransactionsByUserId) => React.ReactElement | null;
}

export const FindTransactionsByUserId: React.FC<Props> = ({ children, variables }) => {
  const { data, loading, fetchMore } = useQuery<
    { findTransactionsByUserId: TransactionPage },
    QueryFindTransactionsByUserIdArgs
  >
  (findTransactionsByUserIdQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  useSubscription(newTransactionSubscription);

  const transactions = data?.findTransactionsByUserId?.data || [];
  const page = data?.findTransactionsByUserId?.page || variables.page;
  const pageSize = data?.findTransactionsByUserId?.pageSize || variables.pageSize;
  const total = data?.findTransactionsByUserId?.total || 0;
  const totalPages = data?.findTransactionsByUserId?.totalPages || 0;
  const hasMoreTransactions = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreTransactions) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.findTransactionsByUserId) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findTransactionsByUserId: {
            ...fetchMoreResult.findTransactionsByUserId,
            data: [
              ...(prev?.findTransactionsByUserId?.data || []),
              ...(fetchMoreResult.findTransactionsByUserId.data || [])
            ]
          }
        };
      }
    });
  }, [fetchMore, variables, page, hasMoreTransactions]);

  return children({
    transactions: transactions as any[],
    loading,
    loadMore,
    hasMoreTransactions,
    page,
    pageSize,
    total,
    totalPages,
  });
};